import { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { doc, onSnapshot, getFirestore } from 'firebase/firestore';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { setField, startEdit, finishEdit } from 'actions/matrixSettings';
import { saveMatrix, fetchMatrix, invalidateMatrix } from 'actions/matrices';
import { validate } from 'actions/matrixSettings';
import { canEdit } from 'constants/matrices';
import { get as getInUse, save as saveInUse } from 'services/in-use';
import moment from 'moment';

const useCanEdit = () => {
  const { keys } = useSelector(state => state.anonymousEditingKeys);
  const { user, userData } = useSelector(state => state.auth);

  return useMemo(() => {
    return matrix => {
      return canEdit(matrix, user, userData, keys[matrix?.id]);
    };
  }, [keys, user, userData]);
};

const useValidateMatix = () => {
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  return useMemo(() => {
    return () => dispatch(validate(user));
  }, [dispatch, user]);
};

// ID is matrix ID, inUse shares a record id with the matrix in a different collection
const useIsInUse = ({ id, hasEditAccess }) => {
  const REFRESH_TIME = 150000;
  const MAX_IDLE_TIME = 300000;

  const dispatch = useDispatch();

  const updateModifiedInterval = useRef(null);

  const { user } = useSelector(state => state.auth);
  const { user_id } = user?.claims ?? { user_id: null };

  const [inUse, setInUse] = useState(null);

  const createInUse = useCallback(
    activeUser => {
      return { id, user: activeUser, modified: moment().toDate() };
    },
    [id]
  );

  const updateInUse = useCallback(
    userId => {
      if (userId === null && updateModifiedInterval.current) {
        clearInterval(updateModifiedInterval.current);
        updateModifiedInterval.current = null;
      }
      const nextObj = createInUse(userId);
      saveInUse(nextObj);
    },
    [createInUse]
  );

  const handleStatusChange = useCallback(
    status => {
      // If the matrix hasn't been set up with the inUse indicator on the backend at all
      if (!status && hasEditAccess) {
        updateInUse(user_id);
        return;
      }
      const isSame = Object.entries(status).every(([k, v]) => {
        if (k === 'modified') return true;
        return inUse?.[k] === v;
      });
      if (!isSame && hasEditAccess) {
        setInUse(status);
      }
    },
    [inUse, user_id, updateInUse, hasEditAccess]
  );

  useEffect(() => {
    let subscription;
    if (id) {
      const db = getFirestore();
      subscription = onSnapshot(doc(db, 'activeMatrices', id), d => {
        const data = d.data();
        if (data) {
          handleStatusChange(data);
        }
      });
    }
    return () => {
      if (subscription) {
        subscription();
      }
    };
  }, [id, handleStatusChange]);

  // Handle updates for when there is no active user, set self as active user
  useEffect(() => {
    if (!inUse) return;

    const { user: activeUser = null, modified = null } = inUse;

    const notInUse = activeUser === null;
    const inUseByAnotherUser = activeUser !== user_id;
    const inUseByCurrentUser = activeUser === user_id;

    if (!hasEditAccess) return;

    if (notInUse) {
      updateInUse(user_id);
    }
    if (inUseByAnotherUser) {
      const modifiedDate = modified.toDate().getTime();
      const currentDate = Date.now();
      const idleTime = currentDate - modifiedDate;
      if (idleTime > MAX_IDLE_TIME) {
        updateInUse(null);
      }
    }
    // If in use by the active user, we want to keep updating the modified date
    if (inUseByCurrentUser && !updateModifiedInterval.current) {
      updateModifiedInterval.current = setInterval(
        () => updateInUse(user_id),
        REFRESH_TIME
      );
    }
  }, [inUse, user_id, updateInUse, hasEditAccess]);

  // If unloading via refresh, back, or closing a tab
  const handleBeforeUnload = useCallback(
    event => {
      if (!inUse) return null;

      const { user: activeUser = null } = inUse;

      if (
        !activeUser ||
        (!!activeUser && activeUser !== user_id) ||
        !hasEditAccess
      ) {
        return null;
      }
      updateInUse(null);
      return null;
    },
    [user_id, inUse, updateInUse, hasEditAccess]
  );

  // On any matrix change, update the event listener for closing a tab
  useEffect(() => {
    // Reset to make sure the matrix it fires with is the latest
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // If navigating away from the matrix, also unset the event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user_id, inUse, handleBeforeUnload]);

  const inUseRef = useRef(null);

  useEffect(() => {
    inUseRef.current = inUse;
  }, [inUse]);

  // onUnmount
  useEffect(() => {
    // Set to null if leaving matrix and this is the active user
    return () => {
      if (
        (!!inUseRef.current && inUseRef.current?.user === user_id) ||
        hasEditAccess
      ) {
        // Need to grab fresh obj because we can't use deps here safely
        getInUse(id).then(v => {
          const latest = v?.[0];
          if (!latest) return;
          if (latest.user === user_id) {
            updateInUse(null);
          }
        });
      }
    };
    // Intentionally has no deps here, we JUST want this to run when leaving the matrix
  }, []);

  const activeUser = useMemo(() => {
    return inUse?.user;
  }, [inUse]);

  useEffect(() => {
    if (activeUser && user_id && activeUser === user_id) {
      dispatch(invalidateMatrix(id));
      dispatch(fetchMatrix(id));
    }
  }, [dispatch, activeUser, user_id, id]);

  return inUse;
};

export { useCanEdit, useValidateMatix, useIsInUse };
